import { Modal } from "react-bootstrap";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import Select from "react-select";
import configurationAPI from "../../../apis/configuration/configurationAPI";
import Swal from 'sweetalert2';
import { useState } from "react";

const inputTypeMapper = {
    dropdown: 'Dropdown',
    text: 'Text',
    number: 'Number',
    date: 'Date'
}

function EditModal({ onHide, show, hccFieldsEditData, setInvalidateHCCFields }) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(null);
    const {
        handleSubmit,
        watch,
        register,
        control,
        getValues,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            isMultiSelect: { value: hccFieldsEditData?.isMultiSelect ?? false, label: hccFieldsEditData?.isMultiSelect === true ? 'Yes' : 'No' },
            type: { value: hccFieldsEditData?.type, label: hccFieldsEditData.type ?? inputTypeMapper[hccFieldsEditData.type] },
            validation: { value: hccFieldsEditData?.validation, label: hccFieldsEditData?.validation},
            name: hccFieldsEditData?.name,
            dropdown: hccFieldsEditData?.HCCDropdownOptions?.map((item) => ({ id: item.id, name: item.name})),
            preserveOnNextRecord: hccFieldsEditData?.preserveOnNextRecord,
        }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: "dropdown",
    });

    const onSubmit = async (data) => {
        try {
            setIsUpdating(true);
            const reqBody = {
                id: hccFieldsEditData?.id,
                name: data?.name,
                type: data?.type?.value,
                isMultiSelect: data?.isMultiSelect?.value || false,
                options: data?.dropdown?.map((item) => ({ id: item?.id, name: item?.name })),
                validation: data?.validation?.value,
                preserveOnNextRecord: data?.preserveOnNextRecord ?? false,
            }
            const response = await configurationAPI.updateHCCFields(reqBody);
            if (response?.success) {
                window.toastr.success(response?.message);
                setInvalidateHCCFields(prev => !prev);
                handleHideModal();
            } else {
                window.toastr.error(response?.message);
            }
        } catch (error) {
            window.toastr.error(error?.message || "Couldn't update fields.");
        }
        setIsUpdating(false);

    }

    const handleHideModal = () => {
        reset({
            name: '',
            type: '',
            isMultiSelect: '',
            dropdown: '',
            validation: '',
        });
        onHide();
    }

    const renderBoolean = () => ([
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
    ]);

    const renderTypes = () => ([
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
        { value: 'dropdown', label: 'Dropdown' },
    ]);

    const renderValidation = () => ([
        { value: "Mandatory", label: 'Mandatory' },
        { value: "Non-Mandatory", label: "Non-Mandatory" },
        { value: "Not-Applicable", label: "Not-Applicable" },
    ]);

    const handleRemoveOptions = async (field, index) => {
        if (!field.name) {
            remove(index);
            return;
        }
        Swal.fire({
            text: `Do you want to delete selected item?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-sm btn-primary',
                cancelButton: 'btn btn-sm btn-secondary',
            },
        }).then(async (result) => {
            if (result?.value === true) {
                setIsDeleting(index);
                try {
                    const optionToDelete = getValues('dropdown').find((item) => ((item.name === field.name) && (item.id)));
                    if (optionToDelete) {
                        const reqBody = {
                            id: hccFieldsEditData?.id,
                            toDelete: optionToDelete.id
                        }
                        const res = await configurationAPI.updateHCCFields(reqBody);
                        if (res.success) {
                            window.toastr.success(res?.message);
                            setInvalidateHCCFields(prev => !prev);
                            remove(index);
                        } else {
                            window.toastr.error(res?.message);
                        }
                    } else {
                        remove(index);
                    }
                   
               } catch (error) {
                    window.toastr.error(error?.message);
                }
                setIsDeleting(null);
            }
        })
    }

    return (
        <Modal show={show} onHide={handleHideModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit HCC Field</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form className="row" onSubmit={handleSubmit(onSubmit)}>
                    <div className="col-6">
                        <label className="form-label required">Name</label>
                        <input
                            type="text"
                            className="form-control"
                            {...register("name", { required: { value: true, message: 'Required' } })}
                        />
                        {
                            errors.name && <p className="text-danger fs-7">{errors.name.message}</p>
                        }
                    </div>

                    <div className="col-6">
                        <label className="form-label required">Type</label>
                        <Controller
                            name="type"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderTypes()}
                                    isDisabled
                                />
                            )}
                        />
                        { errors.type && <p className="text-danger fs-7">{errors.type.message}</p>}
                    </div>

                    <div className="col-6 mt-4 mb-2">
                        <label className="form-label required">Validation</label>
                        <Controller
                            name="validation"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderValidation()}
                                />
                            )}
                        />
                        {
                            errors.validation && <p className="text-danger fs-7">{errors.validation.message}</p>
                        }
                    </div>

                    <div className="col-6 mt-4 mb-2 d-flex align-items-end">
                        <div className="form-check d-flex align-items-center gap-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="preserveOnNextRecord"
                                {...register("preserveOnNextRecord")}
                            />
                            <label className="form-check-label" htmlFor="preserveOnNextRecord">
                                Preserve for next record
                            </label>
                        </div>
                    </div>

                    {watch('type')?.value === 'dropdown' && <div className="col-12 mt-4">
                        <label className="form-label required">Is Multi Select</label>
                        <Controller
                            name="isMultiSelect"
                            control={control}
                            rules={{ required: { value: true, message: 'Required' } }}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    value={value}
                                    onChange={(value) => onChange(value)}
                                    components={{ IndicatorSeparator: () => null }}
                                    options={renderBoolean()}
                                    defaultValue={{ value: false, label: 'No' }}
                                    isDisabled
                                />
                            )}
                        />
                        {
                            errors.isMultiSelect && <p className="text-danger fs-7">{errors.isMultiSelect.message}</p>
                        }
                    </div>}

                    <div className="col gap-3">
                        {watch('type')?.value === 'dropdown' && <div className="col-6 mt-4 mb-2">
                            <button
                                type="button"
                                onClick={() => append({ name: '' })} className="btn btn-secondary btn-sm form-control d-flex gap-2 align-items-center justify-content-center"
                            >
                                Add Options
                                <i className="fa fa-plus" />
                            </button>
                        </div>}

                        {watch('type')?.value === 'dropdown' && fields?.map((field, index) => (
                            <div key={field.id} className="row">
                                <div className="mt-1 form-group d-flex flex-wrap align-items-center gap-2">
                                    <input
                                        type="text"
                                        className="form-control mw-100 w-75 mb-1"
                                        {...register(`dropdown.${index}.name`, { required: { value: true, message: 'Required' } })}
                                    />
                                    {isDeleting === field.id && <span className="span spinner-border spinner-border-sm" />}
                                    <button
                                        className="btn btn-sm btn-icon btn-light-danger"
                                        onClick={() => handleRemoveOptions(field, index)}
                                        type="button"
                                    >
                                        x
                                    </button>
                                </div>
                                {errors?.dropdown?.[index]?.name && <p className="text-danger fs-7">{errors?.dropdown?.[index]?.name?.message}</p>}
                            </div>))
                        }
                    </div>

                    <div className="d-flex gap-3 align-items-center justify-content-end mt-4" >
                        <button
                            type="button"
                            className="btn btn-light mt-4 btn-sm"
                            onClick={handleHideModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary mt-4 btn-sm"
                        >
                            Save {isUpdating && <span className="span spinner-border spinner-border-sm" />}
                        </button>
                    </div>

                </form>
            </Modal.Body>
        </Modal>
    )
}

export default EditModal;